import React, { useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import {
  regenerateSingleBoldLinkForOrder,
  regenerateBoldLinkForPartialRow,
  type BoldRegenerateSaveRetry
} from '../../../services/payments/boldRegenerateLink'
import { retrySaveBoldTransactionOnOrder } from '../../../services/payments/boldSingleOrderLinkFlow'
import { retrySaveBoldPartialLinksOnOrder } from '../../../services/payments/boldPartialLinksAfterOrderSave'
import { BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED } from '../../../services/payments/boldPatchRetry'

interface PendingBoldSave {
  transactionId: string
  url: string
  saveRetry: BoldRegenerateSaveRetry
}

interface BoldRegenerateLinkButtonProps {
  orderId: number
  variant: 'main' | 'partial'
  rowIndex?: number
  /** Se invoca al hacer clic para usar totales/precios actuales del formulario */
  getTotalAmount: () => number
  /** Solo variant partial: abonos actuales del formulario para el PATCH */
  getPagosFromForm?: () => Array<Record<string, unknown>>
  storeName: string
  status: string
  paymentMethod?: string
  disabled?: boolean
  onRegenerated: (transactionId: string, url: string) => void
}

export function BoldRegenerateLinkButton({
  orderId,
  variant,
  rowIndex,
  getTotalAmount,
  getPagosFromForm,
  storeName,
  status,
  paymentMethod,
  disabled = false,
  onRegenerated
}: BoldRegenerateLinkButtonProps) {
  const [loading, setLoading] = useState(false)
  const [retryingSave, setRetryingSave] = useState(false)
  const [pendingSave, setPendingSave] = useState<PendingBoldSave | null>(null)

  const handleClick = useCallback(async () => {
    if (disabled || loading) return
    const totalAmount = Math.round(getTotalAmount())
    if (totalAmount <= 0) {
      toast.error('El total del pedido debe ser mayor a 0 para generar el link Bold', {
        icon: '⚠️',
        position: 'top-center'
      })
      return
    }
    setLoading(true)
    try {
      const result =
        variant === 'main'
          ? await regenerateSingleBoldLinkForOrder({
              orderId,
              totalAmount,
              storeName,
              status,
              paymentMethod: paymentMethod || 'bold'
            })
          : await regenerateBoldLinkForPartialRow({
              orderId,
              rowIndex: rowIndex ?? 0,
              totalAmount,
              pagosFromForm: getPagosFromForm?.()
            })

      if (!result.ok) {
        if (result.saveRetry && result.transactionId) {
          setPendingSave({
            transactionId: result.transactionId,
            url: result.url || '',
            saveRetry: result.saveRetry
          })
        } else {
          setPendingSave(null)
        }
        toast.error(result.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED, {
          icon: '⚠️',
          position: 'top-center',
          duration: 8000
        })
        return
      }

      setPendingSave(null)
      const txId = result.transactionId || ''
      const url = result.url || ''
      if (txId) {
        onRegenerated(txId, url)
      }
      toast.success('Nuevo link Bold generado', { icon: '✅', position: 'top-center' })
    } catch (err) {
      console.error('Error regenerando link Bold:', err)
      toast.error('Error al generar el nuevo link Bold', {
        icon: '⚠️',
        position: 'top-center'
      })
    } finally {
      setLoading(false)
    }
  }, [
    disabled,
    loading,
    variant,
    orderId,
    getTotalAmount,
    getPagosFromForm,
    storeName,
    status,
    paymentMethod,
    rowIndex,
    onRegenerated
  ])

  const handleRetrySave = useCallback(async () => {
    if (!pendingSave || retryingSave) return
    setRetryingSave(true)
    try {
      const res =
        pendingSave.saveRetry.kind === 'single'
          ? await retrySaveBoldTransactionOnOrder(pendingSave.saveRetry.orderId, pendingSave.saveRetry.context)
          : await retrySaveBoldPartialLinksOnOrder(pendingSave.saveRetry.orderId, pendingSave.saveRetry.context)
      if (!res.ok) {
        toast.error(res.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED, {
          icon: '⚠️',
          position: 'top-center',
          duration: 8000
        })
        return
      }
      const txId = pendingSave.transactionId
      const url = pendingSave.url
      setPendingSave(null)
      if (txId) onRegenerated(txId, url)
      toast.success('Enlace guardado en el pedido', { icon: '✅', position: 'top-center' })
    } finally {
      setRetryingSave(false)
    }
  }, [pendingSave, retryingSave, onRegenerated])

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <span className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled || loading}
          className="px-2 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs disabled:cursor-not-allowed disabled:opacity-50"
          title={
            disabled
              ? 'No disponible: el pago ya está verificado'
              : 'Genera un nuevo link de pago Bold (el anterior dejará de usarse)'
          }
          aria-label="Generar nuevo link de pago Bold"
        >
          {loading ? 'Generando…' : 'Nuevo link'}
        </button>
        {pendingSave && (
          <button
            type="button"
            onClick={handleRetrySave}
            disabled={retryingSave}
            className="px-2 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-xs disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Reintentar guardado del enlace Bold en el pedido"
          >
            {retryingSave ? 'Guardando…' : 'Reintentar guardado'}
          </button>
        )}
      </span>
      {pendingSave && (
        <span role="alert" className="max-w-xs text-xs text-amber-800">
          {BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED}
        </span>
      )}
    </span>
  )
}
